import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useMenuManagement } from 'hooks/useMenuManagement';
import { checkIntegrationIsActive } from 'helpers/integrations';

import MenuActions from 'components/MenuManagement/MenuActions/MenuActions';
import MenuInformation from '../MenuInformation';

const EXTERNAL_INTEGRATION_NAME = 'external_integration';
const POS_TYPE = 'pos';
const ENFORCE_POS_CONDITIONS = 'enforcePosConditions';

export default function MenuManagementActionsWrapper({ merchantID, children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [posOption, setPosOption] = useState({});
  const {
    autoSaveLabel,
    getSupportedIntegrationsFromState,
    getSupportedIntegrationsLoadingState,
    getSupportedIntegrationsErrorState,
    getMerchantIntegrationsFromState,
    getMerchantIntegrationsLoadingState,
    getMerchantIntegrationsErrorState,
    posResource,
  } = useMenuManagement();

  useEffect(() => {
    setIsLoading(
      getSupportedIntegrationsLoadingState() || getMerchantIntegrationsLoadingState(merchantID),
    );
  }, [getSupportedIntegrationsLoadingState, getMerchantIntegrationsLoadingState, merchantID]);

  const hasIntegrationError =
    getSupportedIntegrationsErrorState() || getMerchantIntegrationsErrorState(merchantID);

  const checkPosConfiguration = async () => {
    if (!posResource?.type) return;

    const allSupportedIntegrations = await getSupportedIntegrationsFromState();

    let integrationNameToUse = posResource.type;

    // If type is "external_integration", find the actual POS integration name
    if (posResource.type === EXTERNAL_INTEGRATION_NAME) {
      const merchantIntegrations = await getMerchantIntegrationsFromState(merchantID, false);
      const posIntegration = allSupportedIntegrations.find(integration => {
        const merchantInt = merchantIntegrations?.[integration.integrationName];
        return (
          integration.integrationType === POS_TYPE &&
          merchantInt &&
          checkIntegrationIsActive(merchantInt)
        );
      });

      if (posIntegration) {
        integrationNameToUse = posIntegration.integrationName;
      }
    }

    // Find readable name from supported integrations
    const supportedIntegration = allSupportedIntegrations.find(
      int => int.integrationName === integrationNameToUse,
    );

    setPosOption({
      integrationName: integrationNameToUse,
      readableAppName: supportedIntegration?.readableAppName || integrationNameToUse,
    });
  };

  useEffect(() => {
    if (!isLoading && !hasIntegrationError && posResource) {
      checkPosConfiguration();
    }
  }, [isLoading, hasIntegrationError, posResource]);

  const createListOfAvailableIntegrations = async merchantId => {
    if (hasIntegrationError) {
      return [];
    }

    // this includes app integrations and the company's POS
    const merchantIntegrations = await getMerchantIntegrationsFromState(merchantId, false);
    const allSupportedMenuManagementIntegrations = await getSupportedIntegrationsFromState();

    const menuManagementPullSupportedObj = allSupportedMenuManagementIntegrations
      .filter(integration => integration.isSupportedPull)
      .reduce((acc, integration) => {
        acc[integration.integrationName] = integration;
        return acc;
      }, {});

    // get all merchant integrations except enforcePosConditions
    // (which is a special flag, not an integration)
    // and map them to their full integration details if supported
    return Object.entries(merchantIntegrations || {})
      .filter(([key]) => key !== ENFORCE_POS_CONDITIONS)
      .flatMap(([integrationName, merchantIntegration]) => {
        const integration = menuManagementPullSupportedObj[integrationName];
        if (integration) {
          const { readableAppName, isCubohConnect, useLocalPull } = integration;
          return [
            {
              integrationName,
              readableAppName,
              isCubohConnect,
              useLocalPull,
              id: merchantIntegration?.activeData?.id,
            },
          ];
        }
        return [];
      });
  };

  return (
    <MenuInformation autoSaveLabel={autoSaveLabel}>
      <MenuActions
        isLoading={isLoading}
        posOption={posOption}
        createListOfAvailableIntegrations={createListOfAvailableIntegrations}
      >
        {children}
      </MenuActions>
    </MenuInformation>
  );
}

MenuManagementActionsWrapper.propTypes = {
  merchantID: PropTypes.string,
  children: PropTypes.node,
};
